
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll to add background/shadow when page is scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to a section safely; fallback if element is not found
  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (!element) {
        console.warn(`Navbar: No element found with id="${id}"`);
        return;
      }
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Navbar: scrollToSection error', error);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo – scrolls to the hero section */}
          <button
            onClick={() => scrollToSection('hero')}
            className="flex items-center space-x-3 cursor-pointer whitespace-nowrap"
          >
            <img
              src="https://static.readdy.ai/image/2299103fa86076f2ed06076e2d6cbff8/b0216ecd9824ced9791064a8a701a53c.png"
              alt="ROBO Asset Grid Logo"
              className="h-12 w-auto"
            />
          </button>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Products
            </button>
            <button
              onClick={() => scrollToSection('catalog')}
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Catalog
            </button>
            <button
              onClick={() => scrollToSection('social-issues')}
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Technology
            </button>
            <button
              onClick={() => scrollToSection('business-plan')}
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Business
            </button>
            <button
              onClick={() => scrollToSection('patents')}
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Patents
            </button>
            <Link
              to="/"
              className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              한국어
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-6 py-2.5 bg-teal-600 text-white text-base font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
            aria-label="Toggle navigation menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile navigation panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              Products
            </button>
            <button
              onClick={() => scrollToSection('catalog')}
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              Catalog
            </button>
            <button
              onClick={() => scrollToSection('social-issues')}
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              Technology
            </button>
            <button
              onClick={() => scrollToSection('business-plan')}
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              Business
            </button>
            <button
              onClick={() => scrollToSection('patents')}
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              Patents
            </button>
            <Link
              to="/"
              className="block w-full text-left px-4 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
            >
              한국어
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full px-4 py-2.5 bg-teal-600 text-white text-base font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
